'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, Check, Copy, Users, Brain, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string;
}

export function WaitlistModal({ isOpen, onClose, source = 'modal' }: WaitlistModalProps) {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [primaryUseCase, setPrimaryUseCase] = useState('');
  const [contentChallenge, setContentChallenge] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/waitlist/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          companyName,
          teamSize,
          primaryUseCase,
          contentChallenge,
          source
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist');
      }

      setReferralCode(data.referralCode);
      setIsSuccess(true);

    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setEmail('');
    setCompanyName('');
    setTeamSize('');
    setPrimaryUseCase('');
    setContentChallenge('');
    setIsSuccess(false);
    setReferralCode('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      console.log('Dialog open state changed:', open); // Debug log
      if (!open) {
        handleClose();
      }
    }}>
      <DialogContent className="sm:max-w-[500px] z-[100]">
        {!isSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Join the Enterprise AI Waitlist
              </DialogTitle>
              <DialogDescription>
                Be among the first to access custom AI that learns your domain,
                terminology, and transforms your team conversations into content that grows your business.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company Name</Label>
                <Input
                  id="company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company"
                  disabled={isSubmitting}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="team-size">Team Size</Label>
                  <Select value={teamSize} onValueChange={setTeamSize} disabled={isSubmitting}>
                    <SelectTrigger id="team-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="z-[150]">
                      <SelectItem value="1-10">1-10</SelectItem>
                      <SelectItem value="11-50">11-50</SelectItem>
                      <SelectItem value="51-200">51-200</SelectItem>
                      <SelectItem value="201-500">201-500</SelectItem>
                      <SelectItem value="500+">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="use-case">Primary Use Case</Label>
                  <Select value={primaryUseCase} onValueChange={setPrimaryUseCase} disabled={isSubmitting}>
                    <SelectTrigger id="use-case">
                      <SelectValue placeholder="Select focus" />
                    </SelectTrigger>
                    <SelectContent className="z-[150]">
                      <SelectItem value="Sales">Sales Teams</SelectItem>
                      <SelectItem value="Engineering">Engineering</SelectItem>
                      <SelectItem value="Customer Success">Customer Success</SelectItem>
                      <SelectItem value="Executive">Executive/Strategy</SelectItem>
                      <SelectItem value="Marketing">Marketing</SelectItem>
                      <SelectItem value="Cross-functional">Cross-functional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="challenge">
                  What's your biggest content challenge? (Optional)
                </Label>
                <Textarea
                  id="challenge"
                  value={contentChallenge}
                  onChange={(e) => setContentChallenge(e.target.value)}
                  placeholder="e.g., Our technical team has amazing insights but struggles to share them externally..."
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Joining...' : 'Join Waitlist'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                We'll send you monthly updates about our progress. No spam, unsubscribe anytime.
              </p>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                You're on the list!
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <p className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
                  Welcome to the future of B2B content transformation
                </p>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  We'll keep you updated on our custom AI development and give you
                  early access when we launch enterprise features.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-sm">What happens next:</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Brain className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      We'll analyze your use case to customize AI for your needs
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Monthly updates on product development and feature previews
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Building2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Priority access and founding member pricing when we launch
                    </p>
                  </div>
                </div>
              </div>

              {referralCode && (
                <div className="p-3 border rounded-lg bg-muted/30">
                  <p className="text-xs font-medium mb-2">Share with your team:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-2 py-1 bg-background rounded text-xs">
                      {window.location.origin}?ref={referralCode}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyReferralLink}
                      className="h-7"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              )}

              <Button onClick={handleClose} className="w-full">
                Got it, thanks!
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}